import { OrderingProvider } from 'models/Merchant'
import { api } from './index'

export enum MenuErrorSeverity {
  Error = 'error',
  Notice = 'notice',
  Info = 'info',
}

export type SkippedProvider = OrderingProvider | null

export type MenuErrorCounts = {
  error: number
  notice: number
  // Only present when the caller passed `?include=info` AND has the
  // `manage` permission. List view never includes it because the
  // bucket is hidden by default.
  info?: number
}

export type MenuLocationStub = {
  id: number
  name: string
  // Address fields are additive — the gRPC contract from DATA-5258
  // doesn't carry them today, but the modal renders them when present
  // (per Figma 432:5945). For now they come from a follow-up BE patch
  // or are joined client-side; the mock layer fills them in for the
  // canned dataset.
  street?: string
  city?: string
  state?: string
  zip?: string
}

// Item-level entry surfaced when the operator expands a row in the
// detail view — matches the canonical Figma frame 432:2419 (item name
// + raw POS UUID so a Toast admin can copy/paste into their dashboard
// search). Optional because the BE doesn't yet emit this; populated
// only by the mock layer until a follow-up extends the gRPC handler.
export type MenuErrorItem = {
  name: string
  uuid: string
}

export type MenuErrorOccurrence = {
  message: string
  severity: MenuErrorSeverity
  locationCount: number
  // Per Decision 3 (2026-05-15) — errors are not uniform within a menu
  // name, so each occurrence carries the locations it applies to. Used
  // by the detail view's bi-directional filter; absent on the list
  // response.
  locationIds: number[]
  // Optional secondary copy under the row title (Figma 432:2419 calls
  // this "Skipped due to unsupported pricing strategy 'SIZE_PRICE'.").
  // BE doesn't classify into title/description yet so this is
  // additive and only set by the mock layer.
  description?: string
  // Items that triggered the occurrence — drives the accordion-
  // expanded content. Same caveat as `description`: additive, mock-
  // only until a BE follow-up wires it.
  items?: MenuErrorItem[]
}

export type MenuErrorSummary = {
  name: string
  locationCount: number
  configErrorsCount: MenuErrorCounts
  lastSyncedAt: string | null
}

// Providers that contribute to the merchant's POS configuration. The
// list endpoint returns Toast-grouped menus today; Olo lives at the
// per-location level. When a merchant uses both providers the FE
// surfaces a tab nav (Figma 432:2630) so the operator can hop between
// "menu-grouped Toast errors" and the "Olo errors live on Locations"
// explanation without losing the page header. Single-provider responses
// can leave this absent — the existing text section header still fires.
export type PosProvider = 'toast' | 'olo'

export type MenuErrorsListResponse = {
  menus: MenuErrorSummary[]
  skippedProvider: SkippedProvider
  providers?: PosProvider[]
}

export type MenuErrorDetail = {
  name: string
  locationCount: number
  locations: MenuLocationStub[]
  errors: MenuErrorOccurrence[]
  notices: MenuErrorOccurrence[]
  // Only populated when the caller passes `?include=info` and has the
  // `manage` permission. Hidden by default per the locked design.
  info?: MenuErrorOccurrence[]
  lastSyncedAt: string | null
}

export type GetMenuErrorDetailParams = {
  menuName: string
  // `true` adds `?include=info` to the request. The BE gates the param
  // on `manage` permission; the FE doesn't pre-check because RBAC state
  // can shift between page load and request (multi-merchant switch).
  includeInfo?: boolean
}

// --- Wire shapes ---------------------------------------------------------
// The BE responds in snake_case. Per the project convention (CLAUDE.md:
// "normalize to camelCase at the API/slice layer, never use snake_case in
// components"), the raw wire payload is typed here and mapped to the
// camelCase domain types above inside `transformResponse`, so snake_case
// never leaks past this module. Mirrors OC Locations' normaliser pattern.
// Types with no snake_case fields (MenuErrorCounts, MenuLocationStub,
// MenuErrorItem) are reused as-is.

type WireMenuErrorOccurrence = {
  message: string
  severity: MenuErrorSeverity
  location_count: number
  location_ids: number[]
  description?: string
  items?: MenuErrorItem[]
}

type WireMenuErrorSummary = {
  name: string
  location_count: number
  config_errors_count: MenuErrorCounts
  last_synced_at: string | null
}

type WireMenuErrorsListResponse = {
  menus: WireMenuErrorSummary[]
  skipped_provider: SkippedProvider
  providers?: PosProvider[]
}

type WireMenuErrorDetail = {
  name: string
  location_count: number
  locations: MenuLocationStub[]
  errors: WireMenuErrorOccurrence[]
  notices: WireMenuErrorOccurrence[]
  info?: WireMenuErrorOccurrence[]
  last_synced_at: string | null
}

const normalizeOccurrence = (
  o: WireMenuErrorOccurrence
): MenuErrorOccurrence => ({
  message: o.message,
  severity: o.severity,
  locationCount: o.location_count,
  locationIds: o.location_ids,
  description: o.description,
  items: o.items,
})

const normalizeSummary = (m: WireMenuErrorSummary): MenuErrorSummary => ({
  name: m.name,
  locationCount: m.location_count,
  configErrorsCount: m.config_errors_count,
  lastSyncedAt: m.last_synced_at,
})

const normalizeListResponse = (
  r: WireMenuErrorsListResponse
): MenuErrorsListResponse => ({
  menus: r.menus.map(normalizeSummary),
  skippedProvider: r.skipped_provider,
  providers: r.providers,
})

const normalizeDetail = (d: WireMenuErrorDetail): MenuErrorDetail => ({
  name: d.name,
  locationCount: d.location_count,
  locations: d.locations,
  errors: d.errors.map(normalizeOccurrence),
  notices: d.notices.map(normalizeOccurrence),
  info: d.info?.map(normalizeOccurrence),
  lastSyncedAt: d.last_synced_at,
})

export const menuErrorsApi = api.injectEndpoints({
  endpoints: builder => ({
    getMenuErrors: builder.query<MenuErrorsListResponse, void>({
      query: () => ({
        url: 'menu_errors',
      }),
      transformResponse: (raw: WireMenuErrorsListResponse) =>
        normalizeListResponse(raw),
      providesTags: ['MenuErrors'],
    }),
    getMenuErrorDetail: builder.query<
      MenuErrorDetail,
      GetMenuErrorDetailParams
    >({
      query: ({ menuName, includeInfo }) => ({
        // `menuName` may contain spaces or punctuation (Toast lets
        // operators name a menu "Bad-Ass Breakfast Burritos"); the
        // BE detail route matches on the encoded segment.
        url: `menu_errors/${encodeURIComponent(menuName)}`,
        params: includeInfo ? { include: 'info' } : undefined,
      }),
      transformResponse: (raw: WireMenuErrorDetail) => normalizeDetail(raw),
      providesTags: (_result, _err, { menuName }) => [
        { type: 'MenuErrors', id: menuName },
      ],
    }),
  }),
})

export const { useGetMenuErrorsQuery, useGetMenuErrorDetailQuery } =
  menuErrorsApi
